/**
 * i18next configuration. Loads all 10 locale bundles at startup (they're small),
 * detects browser language on first visit, and persists the user's choice in
 * localStorage under the `territoria.lang` key.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import zh from "./locales/zh.json";
import es from "./locales/es.json";
import pt from "./locales/pt.json";
import ru from "./locales/ru.json";
import de from "./locales/de.json";
import fr from "./locales/fr.json";
import ja from "./locales/ja.json";
import ko from "./locales/ko.json";
import ar from "./locales/ar.json";

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English",    native: "English",    rtl: false, flag: "🇬🇧" },
  { code: "zh", name: "Chinese",    native: "中文",        rtl: false, flag: "🇨🇳" },
  { code: "es", name: "Spanish",    native: "Español",    rtl: false, flag: "🇪🇸" },
  { code: "pt", name: "Portuguese", native: "Português",  rtl: false, flag: "🇧🇷" },
  { code: "ru", name: "Russian",    native: "Русский",    rtl: false, flag: "🇷🇺" },
  { code: "de", name: "German",     native: "Deutsch",    rtl: false, flag: "🇩🇪" },
  { code: "fr", name: "French",     native: "Français",   rtl: false, flag: "🇫🇷" },
  { code: "ja", name: "Japanese",   native: "日本語",      rtl: false, flag: "🇯🇵" },
  { code: "ko", name: "Korean",     native: "한국어",      rtl: false, flag: "🇰🇷" },
  { code: "ar", name: "Arabic",     native: "العربية",   rtl: true,  flag: "🇸🇦" },
];

export const DEFAULT_LANGUAGE = "en";
const STORAGE_KEY = "territoria.lang";

/** Sync the <html> dir / lang attributes for proper RTL rendering and a11y. */
function applyHtmlDir(lang) {
  const meta = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0];
  document.documentElement.lang = meta.code;
  document.documentElement.dir = meta.rtl ? "rtl" : "ltr";
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      zh: { translation: zh },
      es: { translation: es },
      pt: { translation: pt },
      ru: { translation: ru },
      de: { translation: de },
      fr: { translation: fr },
      ja: { translation: ja },
      ko: { translation: ko },
      ar: { translation: ar },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true,   // accept "pt-BR" → "pt", "zh-CN" → "zh", etc.
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
  });

// First-time + subsequent language changes both go through this hook,
// keeping the <html dir> attribute in sync with the active language.
i18n.on("languageChanged", applyHtmlDir);
applyHtmlDir(i18n.language);

export default i18n;
